import { useState } from 'react'
import type { IPrimer } from '../../Model/index.js'

export interface PrimerChoiceProps {
  /** Most specific first; the first is preselected. */
  candidates: IPrimer[]
  onChoose: (primer: IPrimer) => void
  confirmLabel?: string
  disabled?: boolean
}

/** Picks one of several registry primer pairs that all match the samples' primer sequences. */
export const PrimerChoice = ({ candidates, onChoose, confirmLabel = 'Use this primer pair', disabled = false }: PrimerChoiceProps) => {
  const [selected, setSelected] = useState(candidates[0]?.id)
  const chosen = candidates.find((primer) => primer.id === selected)

  return (
    <div className="space-y-2" data-testid="primer-choice">
      <p className="text-sm">
        Several registered primer pairs match the primer sequences of these samples. Choose the pair they were amplified with; it sets the
        amplicon length filter and the literature reference.
      </p>
      <ul className="space-y-1">
        {candidates.map((primer) => (
          <li key={primer.id}>
            <label className="flex cursor-pointer items-start gap-2 rounded border border-slate-200 p-2 text-sm">
              <input
                type="radio"
                className="radio radio-sm mt-0.5"
                name="primer-choice"
                checked={primer.id === selected}
                disabled={disabled}
                onChange={() => setSelected(primer.id)}
                data-testid={`primer-choice-${primer.id}`}
              />
              <span>
                <span className="font-bold">
                  {primer.forwardName} / {primer.reverseName}
                </span>
                <span className="block font-mono text-xs break-all">
                  {primer.forwardSequence} / {primer.reverseSequence}
                </span>
                <span className="block text-xs text-slate-600">
                  {primer.basePairLength}
                  {primer.maxBasePairLength ? `–${primer.maxBasePairLength}` : ''} bp · {primer.reference}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        disabled={disabled || !chosen}
        onClick={() => chosen && onChoose(chosen)}
        data-testid="primer-choice-confirm"
      >
        {confirmLabel}
      </button>
    </div>
  )
}
